<template>
  <div class="upgrade-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">设备升级</h1>
        <p class="page-subtitle">选择设备类型，上传升级文件</p>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 设备类型选择 -->
      <div class="section">
        <h2 class="section-title">选择设备类型</h2>
        <div class="device-grid">
          <div v-for="device in deviceTypes" :key="device.id" class="device-item"
            :class="{ active: selectedDevice === device.id }" @click="selectDevice(device.id)">
            <div class="device-icon">
              <i :class="device.icon"></i>
            </div>
            <div class="device-text">
              <h3>{{ device.name }}</h3>
              <p>{{ device.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 版本信息输入 -->
      <div class="section" v-if="selectedDevice">
        <h2 class="section-title">版本信息</h2>
        <div class="version-form">
          <div class="form-row">
            <div class="form-item">
              <label>版本号</label>
              <el-input v-model="versionInfo.version" placeholder="请输入版本号，如：v1.2.3" :disabled="isUploading"></el-input>
            </div>
            <div class="form-item">
              <label>构建号</label>
              <el-input v-model="versionInfo.buildNumber" placeholder="请输入构建号，如：20231201"
                :disabled="isUploading"></el-input>
            </div>
          </div>
          <div class="form-item full-width">
            <label>版本更新说明</label>
            <el-input v-model="versionInfo.description" type="textarea" :rows="4" placeholder="请输入版本更新说明，包括新功能、修复的bug等"
              :disabled="isUploading"></el-input>
          </div>
        </div>
      </div>

      <!-- 文件上传区域 -->
      <div class="section" v-if="selectedDevice">
        <h2 class="section-title">上传升级文件（APK + JSON）</h2>
        

        
        <div class="upgrade-upload">
          <div class="upload-row">
            <!-- APK文件上传 -->
            <div class="upload-item">
              <label>APK应用包：</label>
              <OSSFileUploader
                ref="apkUploader"
                :multiple="false"
                accept=".apk"
                :allowed-types="['apk']"
                :max-file-size="500 * 1024 * 1024"
                placeholder="上传APK文件"
                accept-text="支持 APK 格式，最大 500MB"
                :upload-dir="apkUploadDir"
                :auto-upload="false"
                :show-upload-button="false"
                :use-original-name="true"
                @file-success="handleApkSuccess"
                @upload-error="handleUploadError"
                @file-list-change="handleApkFileListChange"
              />
            </div>
            
            <!-- JSON配置文件上传 -->
            <div class="upload-item">
              <label>JSON配置文件：</label>
              <OSSFileUploader
                ref="jsonUploader"
                :multiple="false"
                accept=".json"
                :allowed-types="['json']"
                :max-file-size="10 * 1024 * 1024"
                placeholder="上传JSON配置文件"
                accept-text="支持 JSON 格式，最大 10MB"
                :upload-dir="jsonUploadDir"
                :auto-upload="false"
                :show-upload-button="false"
                :use-original-name="true"
                @file-success="handleJsonSuccess"
                @upload-error="handleUploadError"
                @file-list-change="handleJsonFileListChange"
              />
            </div>
          </div>
          
          <!-- 上传提示和按钮 -->
          <div class="upload-actions">
            <div class="upload-notice">
              <i class="el-icon-info"></i>
              <span>请同时上传APK应用包和JSON配置文件</span>
            </div>
            <el-button 
              type="primary" 
              size="large" 
              :loading="isUploading" 
              :disabled="!canUpload || isUploading"
              @click="handleUpload" 
              class="upload-btn"
            >
              <i class="el-icon-upload"></i>
              {{ isUploading ? '正在上传...' : '开始升级' }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 升级记录列表 -->
      <div class="section">
        <div class="section-header">
          <h2 class="section-title">升级记录</h2>
          <div class="section-actions">
            <el-button type="text" icon="el-icon-refresh" @click="refreshUpgradeHistory" :loading="loadingHistory">
              刷新
            </el-button>
          </div>
        </div>

        <div class="upgrade-history">
          <el-table :data="upgradeHistory" v-loading="loadingHistory" stripe style="width: 100%">
            <el-table-column prop="version" label="版本号" width="120">
              <template slot-scope="scope">
                <el-tag size="small" type="primary">{{ scope.row.version }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="buildNumber" label="构建号" width="120"></el-table-column>
            <el-table-column prop="deviceType" label="设备类型" width="100">
              <template slot-scope="scope">
                <el-tag size="small" :type="getDeviceTypeTag(scope.row.deviceType)">
                  {{ getDeviceTypeName(scope.row.deviceType) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="description" label="更新说明" show-overflow-tooltip></el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="160"></el-table-column>
            <el-table-column label="操作" width="200" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="viewUpgradeDetail(scope.row)">
                  详情
                </el-button>
                <el-button type="text" size="small" style="color: #f56c6c;" @click="deleteUpgradeRecord(scope.row)">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页组件 -->
          <div class="pagination-container">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
              :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper" :total="pagination.total" background>
            </el-pagination>
          </div>
        </div>
      </div>

      <!-- 上传进度 -->
      <div v-if="isUploading" class="progress-section">
        <el-progress :percentage="uploadProgress" :stroke-width="6" color="#409EFF"></el-progress>
        <p class="progress-text">正在上传文件，请稍候...</p>
      </div>

      <!-- 全局上传遮罩 -->
      <div v-if="isUploading" class="upload-mask">
        <div class="upload-modal">
          <div class="upload-icon">
            <i class="el-icon-upload"></i>
          </div>
          <h3 class="upload-title">文件上传中</h3>
          <p class="upload-subtitle">请勿关闭页面或刷新浏览器</p>
          <div class="upload-progress-container">
            <el-progress :percentage="uploadProgress" :stroke-width="8" :show-text="false"
              color="#409EFF"></el-progress>
            <span class="progress-percentage">{{ uploadProgress }}%</span>
          </div>
          <div class="upload-status">
            <span v-if="uploadProgress < 100">正在上传文件...</span>
            <span v-else class="success-text">上传完成！</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 升级详情对话框 -->
    <el-dialog title="升级详情" :visible.sync="detailDialogVisible" width="700px" :close-on-click-modal="false">
      <div v-if="selectedUpgradeDetail" class="upgrade-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="版本号">
            <el-tag size="small" type="primary">{{ selectedUpgradeDetail.version }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="构建号">
            {{ selectedUpgradeDetail.buildNumber }}
          </el-descriptions-item>
          <el-descriptions-item label="设备类型">
            <el-tag size="small" :type="getDeviceTypeTag(selectedUpgradeDetail.deviceType)">
              {{ getDeviceTypeName(selectedUpgradeDetail.deviceType) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间" :span="2">
            {{ selectedUpgradeDetail.createTime }}
          </el-descriptions-item>
          <el-descriptions-item label="APK文件" :span="2">
            <div class="apk-url-container">
              <el-input :value="selectedUpgradeDetail.apkFileUrl" readonly size="small" class="apk-url-input">
                <el-button slot="append" icon="el-icon-document-copy"
                  @click="copyApkUrl(selectedUpgradeDetail.apkFileUrl)" size="small">
                  复制
                </el-button>
              </el-input>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="JSON配置文件" :span="2">
            <div class="apk-url-container">
              <el-input :value="selectedUpgradeDetail.jsonFileUrl" readonly size="small" class="apk-url-input">
                <el-button slot="append" icon="el-icon-document-copy"
                  @click="copyApkUrl(selectedUpgradeDetail.jsonFileUrl)" size="small">
                  复制
                </el-button>
              </el-input>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="升级说明" :span="2">
            <div class="description-content">
              {{ selectedUpgradeDetail.description }}
            </div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { getUpgradeHistory, deleteUpgradeHistory,saveUpgradeInfo } from "../api";
import OSSFileUploader from "../components/OSSFileUploader.vue";

export default {
  name: "Upgrade",
  components: {
    OSSFileUploader
  },
  data() {
    return {
      isUploading: false,
      uploadProgress: 0,
      selectedDevice: null,
      apkFile: null,
      jsonFile: null,
      apkUploader: null,
      jsonUploader: null,
      // 版本信息
      versionInfo: {
        version: '',
        buildNumber: '',
        description: ''
      },
      // 升级历史相关
      loadingHistory: false,
      upgradeHistory: [],
      detailDialogVisible: false,
      selectedUpgradeDetail: null,
      // 回滚相关
      rollbackDialogVisible: false,
      selectedRollbackVersion: null,
      rollbacking: false,
      // 设备类型配置
      deviceTypes: [
        {
          id: "second-gen",
          name: "二代机",
          description: "第二代智能设备",
          icon: "el-icon-cpu",
          path: "second-gen"
        },
        {
          id: "third-gen",
          name: "三代机",
          description: "第三代智能设备",
          icon: "el-icon-cpu",
          path: "third-gen"
        },
        {
          id: "ice-cream",
          name: "冰淇淋机",
          description: "冰淇淋制作设备",
          icon: "el-icon-cold-drink",
          path: "ice-cream"
        }
      ],
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      }
    };
  },
  computed: {
    canUpload() {
      return this.selectedDevice &&
        this.apkFile && this.jsonFile &&
        this.versionInfo.version &&
        this.versionInfo.buildNumber;
    },
    // 动态生成APK文件的upload-dir
    apkUploadDir() {
      if (!this.selectedDevice) {
        return 'equipment-upgrade/apk';
      }
      return `upgrade/${this.selectedDevice}`;
    },
    // 动态生成JSON文件的upload-dir
    jsonUploadDir() {
      if (!this.selectedDevice) {
        return 'equipment-upgrade/config';
      }
      return `upgrade/${this.selectedDevice}`;
    }
  },
  mounted() {
    this.loadUpgradeHistory();
  },
  methods: {
    // 选择设备
    selectDevice(deviceId) {
      this.selectedDevice = deviceId;
      this.filterUpgradeHistory();
      this.resetUploadComponents(); // 重置上传组件
    },
    // 处理APK文件上传成功
    handleApkSuccess(result) {
      this.apkFile = result;
      // 移除单个文件上传成功的提示，只在最终完成时显示
    },
    // 处理JSON文件上传成功
    handleJsonSuccess(result) {
      this.jsonFile = result;
      // 移除单个文件上传成功的提示，只在最终完成时显示
    },
    // 处理上传错误
    handleUploadError(error) {
      console.error('上传失败:', error);
      this.$message.error(error.message || '上传失败');
    },
    // 处理APK文件列表变化
    handleApkFileListChange(fileList) {
      if (fileList.length > 0) {
        // 当有文件被选择时，创建一个临时的文件对象
        const fileItem = fileList[0];
        this.apkFile = {
          file: fileItem.file,
          name: fileItem.name,
          size: fileItem.size,
          success: false, // 还未上传成功
          uploading: false
        };
      } else {
        this.apkFile = null;
      }
    },
    // 处理JSON文件列表变化
    handleJsonFileListChange(fileList) {
      if (fileList.length > 0) {
        // 当有文件被选择时，创建一个临时的文件对象
        const fileItem = fileList[0];
        this.jsonFile = {
          file: fileItem.file,
          name: fileItem.name,
          size: fileItem.size,
          success: false, // 还未上传成功
          uploading: false
        };
      } else {
        this.jsonFile = null;
      }
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    // 处理上传
    async handleUpload() {
      if (!this.canUpload) {
        if (!this.apkFile || !this.jsonFile) {
          this.$message.warning("请同时上传APK应用包和JSON配置文件");
          return;
        }
        return;
      }
      this.isUploading = true;
      this.uploadProgress = 0;
      try {
        await this.uploadFileWithOSS();
        this.uploadProgress = 100;
        setTimeout(() => {
          this.$message.success("文件上传成功！");
          this.resetForm();
          this.pagination.currentPage = 1; // 重置到第一页
          this.loadUpgradeHistory();
          this.isUploading = false;
          this.uploadProgress = 0;
        }, 1000);
      } catch (error) {
        console.error("上传失败:", error);
        this.$message.error(error.message || "文件上传失败，请重试");
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },
    // 使用OSS直传上传文件
    async uploadFileWithOSS() {
      try {
        // 获取上传组件实例
        const apkUploader = this.$refs.apkUploader;
        const jsonUploader = this.$refs.jsonUploader;
        
        if (!apkUploader || !jsonUploader) {
          throw new Error('上传组件未初始化');
        }

        // 检查是否有文件被选择
        const apkFileList = apkUploader.fileList;
        const jsonFileList = jsonUploader.fileList;
        
        if (apkFileList.length === 0) {
          throw new Error('请选择APK文件');
        }
        if (jsonFileList.length === 0) {
          throw new Error('请选择JSON配置文件');
        }

        // 开始上传
        this.uploadProgress = 0;
        
        // 上传APK文件
        const apkResults = await apkUploader.startUpload();
        console.log(apkResults,"apkResults"); 
        if (apkResults.length === 0 || !apkResults[0].success) {
          throw new Error('APK文件上传失败');
        }
        this.uploadProgress = 70;

        // 上传JSON文件
        const jsonResults = await jsonUploader.startUpload();
        console.log(jsonResults,"jsonResults"); 
        if (jsonResults.length === 0 || !jsonResults[0].success) {
          throw new Error('JSON文件上传失败');
        }
        this.uploadProgress = 100;

        // 调用后端接口保存升级记录
    
        
        // 创建 FormData 对象，直接发送表单数据
        const formData = new FormData();
        formData.append('version', this.versionInfo.version);
        formData.append('buildNumber', this.versionInfo.buildNumber);
        formData.append('deviceType', this.selectedDevice);
        formData.append('description', this.versionInfo.description);
        formData.append('apkPath', apkResults[0].url);
        formData.append('jsonPath', jsonResults[0].url);
        
        // 添加调试信息
        console.log('发送的表单数据:');
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }
        
        const result = await saveUpgradeInfo(formData);
        console.log(result,"result");
        if(result.data.code === 200){
          this.$message.success('升级信息保存成功');
        }else{
          this.$message.error(result.message || '升级信息保存失败');
        }
      } catch (error) {
        console.error('OSS上传失败:', error);
        throw error;
      }
    },
    // 重置上传组件
    resetUploadComponents() {
      // 清空文件选择
      this.apkFile = null;
      this.jsonFile = null;
      
      // 重置上传组件
      if (this.$refs.apkUploader) {
        this.$refs.apkUploader.clearFiles();
      }
      if (this.$refs.jsonUploader) {
        this.$refs.jsonUploader.clearFiles();
      }
    },
    // 重置表单
    resetForm() {
      this.apkFile = null;
      this.jsonFile = null;
      this.versionInfo = {
        version: '',
        buildNumber: '',
        description: ''
      };
    },
    // 加载升级历史
    async loadUpgradeHistory() {
      this.loadingHistory = true;
      try {
        const params = {
          page: this.pagination.currentPage,
          size: this.pagination.pageSize
        };

        // 如果选择了设备类型，添加到查询参数中
        if (this.selectedDevice) {
          params.deviceType = this.selectedDevice;
        }

        const result = await getUpgradeHistory(params);
        if (result.data.code === 200 && result.data.data) {
          this.upgradeHistory = result.data.data || result.data.total || result.data;
          this.pagination.total = result.data.total || 0;
        } else {
          this.upgradeHistory = [];
          this.pagination.total = 0;
          this.$message.warning(result.message || '暂无升级记录');
        }
      } catch (error) {
        console.error("加载升级历史失败:", error);
        this.upgradeHistory = [];
        this.pagination.total = 0;
        this.$message.error('加载升级历史失败');
      } finally {
        this.loadingHistory = false;
      }
    },
    // 刷新升级历史
    refreshUpgradeHistory() {
      this.pagination.currentPage = 1; // 重置到第一页
      this.loadUpgradeHistory();
    },
    // 查看升级详情
    viewUpgradeDetail(record) {
      this.selectedUpgradeDetail = record;
      this.detailDialogVisible = true;
    },
    // 获取设备类型标签样式
    getDeviceTypeTag(deviceType) {
      const typeMap = {
        'second-gen': 'primary',
        'third-gen': 'success',
        'ice-cream': 'warning'
      };
      return typeMap[deviceType] || 'info';
    },
    // 获取设备类型名称
    getDeviceTypeName(deviceType) {
      const device = this.deviceTypes.find(d => d.id === deviceType);
      return device ? device.name : deviceType;
    },
    // 筛选升级记录 - 通过重新加载数据实现筛选
    filterUpgradeHistory() {
      this.pagination.currentPage = 1; // 重置到第一页
      this.loadUpgradeHistory();
    },
    // 复制文件URL
    copyApkUrl(url) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(() => {
          this.$message.success('文件URL已复制到剪贴板');
        }).catch(() => {
          this.fallbackCopyTextToClipboard(url);
        });
      } else {
        this.fallbackCopyTextToClipboard(url);
      }
    },
    // 降级复制方案
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.$message.success('文件URL已复制到剪贴板');
      } catch (err) {
        console.error('复制失败:', err);
        this.$message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    },

    // 删除升级记录
    async deleteUpgradeRecord(record) {
      try {
        await this.$confirm(
          `确定要删除版本 ${record.version} 的升级记录吗？此操作不可恢复。`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        const result = await deleteUpgradeHistory(record.id);
        if (result.data.code === 200) {
          this.$message.success('删除成功');
          // 重新加载升级历史
          this.loadUpgradeHistory();
        } else {
          this.$message.error(result.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除升级记录失败:', error);
          this.$message.error('删除失败');
        }
      }
    },
    // 处理分页
    handleSizeChange(newSize) {
      this.pagination.pageSize = newSize;
      this.pagination.currentPage = 1; // 切换每页条数时重置到第一页
      this.loadUpgradeHistory();
    },
    handleCurrentChange(newPage) {
      this.pagination.currentPage = newPage;
      this.loadUpgradeHistory();
    }
  }
};
</script>

<style lang="less" scoped>
.upgrade-container {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 0;
}

.page-header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 24px 0;
  text-align: center;

  .header-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 24px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
  }

  .page-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }
}

.main-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 32px 24px;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 20px 0;
    text-align: center;
  }
}



.device-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.device-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #409EFF;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  }

  &.active {
    border-color: #409EFF;
    background: #f0f9ff;

    .device-icon {
      background: #409EFF;

      i {
        color: white;
      }
    }

    .device-text h3 {
      color: #409EFF;
    }
  }

  .device-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;

    i {
      font-size: 18px;
      color: #6b7280;
    }
  }

  .device-text {
    flex: 1;

    h3 {
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 4px 0;
      color: #1f2937;
    }

    p {
      font-size: 14px;
      margin: 0;
      color: #6b7280;
    }
  }
}



.upgrade-upload {
  .upload-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;

    .upload-item {
      label {
        display: block;
        font-weight: 600;
        color: #374151;
        margin-bottom: 8px;
      }
    }
  }
}

.upload-area {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.upload-item {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #409EFF;
  }

  .upload-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    i {
      font-size: 16px;
      color: #409EFF;
      margin-right: 8px;
    }

    span {
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    }
  }

  .upload-content {
    cursor: pointer;

    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100px;
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      border-color: #409EFF;
      background: #f8fafc;
    }

    i {
      font-size: 24px;
      color: #9ca3af;
      margin-bottom: 8px;
    }

    p {
      margin: 0 0 4px 0;
      font-size: 14px;
      color: #374151;
      font-weight: 500;
    }

    span {
      font-size: 12px;
      color: #9ca3af;
    }
  }

  .file-preview {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    i {
      font-size: 20px;
      color: #409EFF;
      margin-right: 12px;
    }

    .file-details {
      flex: 1;

      .file-name {
        display: block;
        font-size: 14px;
        color: #1f2937;
        font-weight: 500;
        margin-bottom: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .file-size {
        font-size: 12px;
        color: #6b7280;
      }
    }

    .remove-btn {
      color: #ef4444;

      &:hover {
        color: #f87171;
      }
    }
  }
}

.upload-actions {
  text-align: center;

  .upload-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    padding: 8px 16px;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 6px;
    color: #0369a1;
    font-size: 14px;

    i {
      margin-right: 6px;
      font-size: 16px;
    }
  }

  .upload-btn {
    padding: 12px 32px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 8px;

    i {
      margin-right: 8px;
    }
  }
}

.progress-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  .progress-text {
    text-align: center;
    margin: 12px 0 0 0;
    color: #6b7280;
    font-size: 14px;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .main-content {
    padding: 20px 16px;
  }

  .section {
    padding: 20px;
  }

  .device-grid {
    grid-template-columns: 1fr;
  }

  .upload-area {
    grid-template-columns: 1fr;
  }

  .page-header {
    padding: 20px 0;

    .page-title {
      font-size: 20px;
    }

    .page-subtitle {
      font-size: 13px;
    }
  }

  .version-form {
    .form-row {
      flex-direction: column;

      .form-item {
        width: 100%;
        margin-right: 0;
        margin-bottom: 16px;
      }
    }
  }
}

// 版本信息表单样式
.version-form {
  .form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;

    .form-item {
      flex: 1;

      label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 8px;
      }
    }
  }

  .form-item {
    &.full-width {
      width: 100%;

      label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 8px;
      }
    }
  }
}

// 升级历史样式
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .section-title {
    margin: 0;
    text-align: left;
  }

  .section-actions {
    .el-button {
      padding: 8px 12px;
      font-size: 14px;
    }
  }
}

.upgrade-history {
  .el-table {
    .el-table__header-wrapper {
      th {
        background-color: #f8fafc;
        color: #1f2937;
        font-weight: 600;
      }
    }

    .el-table__body-wrapper {
      .el-table__row {
        &:hover {
          background-color: #f0f9ff;
        }
      }
    }
  }

  // 分页容器样式
  .pagination-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
    padding: 16px 0;
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    .el-pagination {
      .el-pagination__total {
        font-size: 14px;
        color: #6b7280;
      }

      .el-pagination__sizes {
        .el-select {
          .el-input {
            .el-input__inner {
              border-radius: 6px;
              border: 1px solid #d1d5db;

              &:focus {
                border-color: #409EFF;
              }
            }
          }
        }
      }

      .btn-prev,
      .btn-next {
        border-radius: 6px;
        border: 1px solid #d1d5db;
        background: white;
        color: #6b7280;

        &:hover {
          border-color: #409EFF;
          color: #409EFF;
        }

        &.disabled {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #e5e7eb;
        }
      }

      .el-pager {
        li {
          border-radius: 6px;
          border: 1px solid #d1d5db;
          background: white;
          color: #6b7280;

          &:hover {
            border-color: #409EFF;
            color: #409EFF;
          }

          &.active {
            background: #409EFF;
            border-color: #409EFF;
            color: white;
          }
        }
      }

      .el-pagination__jump {
        .el-input {
          .el-input__inner {
            border-radius: 6px;
            border: 1px solid #d1d5db;

            &:focus {
              border-color: #409EFF;
            }
          }
        }
      }
    }
  }
}

// 升级详情对话框样式
.upgrade-detail {
  .description-content {
    max-height: 200px;
    overflow-y: auto;
    padding: 12px;
    background-color: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    line-height: 1.6;
    color: #374151;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .apk-url-container {
    .apk-url-input {
      .el-input__inner {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 12px;
        color: #1f2937;
        background-color: #f8fafc;
        border: 1px solid #e5e7eb;
        word-break: break-all;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .el-input-group__append {
        .el-button {
          border: none;
          background-color: transparent;
          color: #6b7280;
          padding: 8px 12px;
          font-size: 12px;
          transition: all 0.2s ease;

          &:hover {
            background-color: #f3f4f6;
            color: #409EFF;
          }

          &:active {
            background-color: #e5e7eb;
          }

          i {
            margin-right: 4px;
            font-size: 14px;
          }
        }
      }
    }
  }

  // 优化两列布局下的显示效果
  :deep(.el-descriptions) {
    .el-descriptions__body {
      .el-descriptions__table {
        .el-descriptions__cell {
          &.el-descriptions__label {
            font-weight: 600;
            color: #1f2937;
            background-color: #f8fafc;
          }

          &.el-descriptions__content {
            padding: 12px 16px;
          }
        }
      }
    }
  }
}

// 回滚确认对话框样式
.rollback-confirm {
  .rollback-warning {
    display: flex;
    align-items: center;
    margin-top: 16px;
    padding: 12px;
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    color: #92400e;

    i {
      font-size: 16px;
      margin-right: 8px;
      color: #f59e0b;
    }

    span {
      font-weight: 500;
    }
  }
}

// 对话框样式优化
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    border-bottom: 1px solid #e5e7eb;
    padding: 20px 24px;

    .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid #e5e7eb;
    padding: 16px 24px;
  }
}

// 表格样式优化
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;

  .el-table__header-wrapper {
    th {
      background-color: #f8fafc;
      border-bottom: 1px solid #e5e7eb;
      color: #1f2937;
      font-weight: 600;
    }
  }

  .el-table__body-wrapper {
    .el-table__row {
      td {
        border-bottom: 1px solid #f3f4f6;
      }

      &:hover {
        background-color: #f0f9ff;
      }
    }
  }
}

// 标签样式优化
:deep(.el-tag) {
  border-radius: 6px;
  font-weight: 500;
}

// 按钮样式优化
:deep(.el-button--text) {
  padding: 4px 8px;
  font-size: 13px;

  &:hover {
    background-color: #f3f4f6;
    border-radius: 4px;
  }
}

.upload-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;

  .upload-modal {
    background: white;
    padding: 32px;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    width: 360px;
    text-align: center;
    animation: slideUp 0.3s ease;

    .upload-icon {
      font-size: 56px;
      color: #409EFF;
      margin-bottom: 20px;
      animation: pulse 2s infinite;
    }

    .upload-title {
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 8px;
    }

    .upload-subtitle {
      font-size: 14px;
      color: #6b7280;
      margin-bottom: 32px;
    }

    .upload-progress-container {
      margin-bottom: 24px;
      position: relative;

      .el-progress {
        margin-bottom: 8px;
      }

      .progress-percentage {
        font-size: 16px;
        font-weight: 600;
        color: #409EFF;
      }
    }

    .upload-status {
      font-size: 14px;
      color: #6b7280;

      .success-text {
        color: #67C23A;
        font-weight: 600;
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}
</style>
